import React, { useState, useEffect } from 'react';

const UnsubscribePage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [message, setMessage] = useState('');

  // Get email from URL params on mount
  useEffect(() => {
    // Set page title
    document.title = 'Unsubscribe from ISTQB AI Quiz Emails';
    
    // Extract email from URL search params or hash
    let emailParam = null;
    
    // Try from URL search params first
    const urlParams = new URLSearchParams(window.location.search);
    emailParam = urlParams.get('email');
    
    // If not found, try from hash fragment
    if (!emailParam && window.location.hash.includes('?')) {
      const hashParams = new URLSearchParams(window.location.hash.split('?')[1]);
      emailParam = hashParams.get('email');
    }
    
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
    
    // Debug log
    console.log('📧 UnsubscribePage loaded with email:', emailParam);
  }, []);

  const handleUnsubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    
    try {
      const apiUrl = import.meta.env.DEV 
        ? 'http://localhost:3005/api/unsubscribe'
        : 'https://quiz-backend-1en6.onrender.com/api/unsubscribe';
        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(`Successfully unsubscribed ${email} from all marketing emails.`);
      } else {
        setStatus('error');
        setMessage(data.error || 'Failed to unsubscribe. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
      console.error('Unsubscribe error:', error);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        maxWidth: '500px',
        width: '90%',
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ 
            color: '#333', 
            fontSize: '1.8rem', 
            marginBottom: '0.5rem',
            fontWeight: '600'
          }}>
            📧 Unsubscribe from Emails
          </h1>
          <p style={{ 
            color: '#666', 
            fontSize: '1rem', 
            margin: 0 
          }}>
            We're sorry to see you go! Enter your email to unsubscribe from all marketing emails.
          </p>
        </div>

        {status === 'success' ? (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#f0f9f0',
            border: '2px solid #4caf50',
            borderRadius: '8px',
            color: '#2e7d32'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <p style={{ fontSize: '1.1rem', fontWeight: '500', margin: 0 }}>
              {message}
            </p>
            <p style={{ fontSize: '0.9rem', color: '#555', marginTop: '1rem' }}>
              You will no longer receive marketing emails from us. This may take up to 24 hours to take effect.
            </p>
          </div>
        ) : (
          <form onSubmit={handleUnsubscribe}>
            <div style={{ marginBottom: '1.5rem' }}>
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '1rem',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
                required
                disabled={status === 'loading'}
              />
            </div>

            <button
              type="submit"
              disabled={status === 'loading'}
              style={{
                width: '100%',
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '600',
                color: 'white',
                background: status === 'loading' ? '#ccc' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxSizing: 'border-box'
              }}
              onMouseEnter={(e) => {
                if (status !== 'loading') {
                  (e.target as HTMLButtonElement).style.transform = 'translateY(-1px)';
                  (e.target as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (status !== 'loading') {
                  (e.target as HTMLButtonElement).style.transform = 'translateY(0)';
                  (e.target as HTMLButtonElement).style.boxShadow = 'none';
                }
              }}
            >
              {status === 'loading' ? '⏳ Processing...' : '🚫 Unsubscribe'}
            </button>
          </form>
        )}

        {status === 'error' && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: '#ffebee',
            border: '2px solid #f44336',
            borderRadius: '8px',
            color: '#c62828'
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>❌</div>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>{message}</p>
          </div>
        )}

        <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
          <p style={{ color: '#888', fontSize: '0.8rem', margin: 0 }}>
            Questions? Contact us at{' '}
            <a href="mailto:ai.tester.course@gmail.com" style={{ color: '#667eea', textDecoration: 'none' }}>
              ai.tester.course@gmail.com
            </a>
          </p>
          <p style={{ color: '#aaa', fontSize: '0.7rem', marginTop: '0.5rem', margin: 0 }}>
            © 2025 AI.Nastasia - ISTQB AI Testing Course
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;